import { useEffect, useState } from "react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Search, Plus, Pencil, Trash2, KeyRound, Loader2, Inbox } from "lucide-react";
import { toast } from "sonner";
import { formatTanggal, roleBadgeClass, roleLabel } from "../lib/format";

export default function UsersPage() {
    const { user: me } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ username: "", nama: "", role: "staf_input", password: "" });
    const [submitting, setSubmitting] = useState(false);

    const [confirmDelete, setConfirmDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [resetOpen, setResetOpen] = useState(null);
    const [newPassword, setNewPassword] = useState("");

    const load = async (query = "") => {
        setLoading(true);
        try {
            const { data } = await api.get("/users", { params: query ? { q: query } : {} });
            setItems(data);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);
    useEffect(() => {
        const t = setTimeout(() => load(q), 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [q]);

    const openCreate = () => {
        setEditing(null);
        setForm({ username: "", nama: "", role: "staf_input", password: "" });
        setDialogOpen(true);
    };
    const openEdit = (row) => {
        setEditing(row);
        setForm({ username: row.username, nama: row.nama, role: row.role, password: "" });
        setDialogOpen(true);
    };

    const save = async () => {
        if (!form.nama.trim()) return toast.error("Nama wajib diisi");
        if (!editing) {
            if (!form.username.trim()) return toast.error("Username wajib diisi");
            if (!form.password || form.password.length < 6) return toast.error("Password minimal 6 karakter");
        }
        setSubmitting(true);
        try {
            if (editing) {
                await api.put(`/users/${editing.id}`, { nama: form.nama, role: form.role });
                toast.success("Pengguna diperbarui");
            } else {
                await api.post("/users", {
                    username: form.username.trim().toUpperCase(),
                    password: form.password,
                    nama: form.nama.trim(),
                    role: form.role,
                });
                toast.success("Pengguna ditambahkan");
            }
            setDialogOpen(false);
            load(q);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setSubmitting(false); }
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/users/${confirmDelete.id}`);
            toast.success("Pengguna dihapus");
            setConfirmDelete(null);
            load(q);
        } catch (e) { toast.error(errorMessage(e)); }
        finally { setDeleting(false); }
    };

    const doReset = async () => {
        if (!newPassword || newPassword.length < 6) return toast.error("Password minimal 6 karakter");
        try {
            await api.post(`/users/${resetOpen.id}/reset-password`, { password: newPassword });
            toast.success("Password direset");
            setResetOpen(null);
            setNewPassword("");
        } catch (e) { toast.error(errorMessage(e)); }
    };

    return (
        <div className="space-y-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Kelola Pengguna</h2>
                    <p className="text-sm text-slate-500 mt-1">Kelola akun & role pengguna sistem</p>
                </div>
                <Button
                    onClick={openCreate}
                    data-testid="users-add-button"
                    className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl shadow-sm gap-2"
                >
                    <Plus className="w-4 h-4" /> Tambah Pengguna
                </Button>
            </div>

            <div className="glass-card rounded-2xl p-4">
                <div className="relative max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                        placeholder="Cari pengguna..."
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="users-search-input"
                        className="pl-9 bg-white/70 border-slate-200 focus-visible:ring-teal-500 rounded-xl h-10"
                    />
                </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-slate-200/70 bg-white/80 backdrop-blur-xl shadow-sm">
                <Table data-testid="users-data-table">
                    <TableHeader>
                        <TableRow className="bg-slate-50/70 hover:bg-slate-50/70">
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Username</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nama</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Role</TableHead>
                            <TableHead className="text-xs font-semibold uppercase tracking-wider text-slate-500">Dibuat</TableHead>
                            <TableHead className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 w-40">Aksi</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={5} className="py-12 text-center"><Loader2 className="w-5 h-5 text-teal-600 animate-spin mx-auto" /></TableCell></TableRow>
                        ) : items.length === 0 ? (
                            <TableRow><TableCell colSpan={5} className="py-14 text-center">
                                <div className="flex flex-col items-center gap-2 text-slate-400">
                                    <Inbox className="w-8 h-8" />
                                    <div className="text-sm font-medium">Belum ada pengguna</div>
                                </div>
                            </TableCell></TableRow>
                        ) : items.map((row) => (
                            <TableRow key={row.id} className="border-b border-slate-100/80 hover:bg-teal-50/40 transition-colors">
                                <TableCell className="font-semibold text-slate-900">{row.username}</TableCell>
                                <TableCell className="text-sm text-slate-800">{row.nama}</TableCell>
                                <TableCell>
                                    <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-md border ${roleBadgeClass(row.role)}`}>
                                        {roleLabel(row.role)}
                                    </span>
                                </TableCell>
                                <TableCell className="text-sm text-slate-500">{formatTanggal(row.created_at)}</TableCell>
                                <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                        <button
                                            onClick={() => openEdit(row)}
                                            data-testid={`users-edit-btn-${row.id}`}
                                            className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-teal-700 transition-colors"
                                            title="Edit"
                                        ><Pencil className="w-4 h-4" /></button>
                                        <button
                                            onClick={() => { setResetOpen(row); setNewPassword(""); }}
                                            data-testid={`users-reset-btn-${row.id}`}
                                            className="p-2 rounded-lg text-slate-600 hover:bg-amber-50 hover:text-amber-700 transition-colors"
                                            title="Reset Password"
                                        ><KeyRound className="w-4 h-4" /></button>
                                        {row.id !== me?.id && (
                                            <button
                                                onClick={() => setConfirmDelete(row)}
                                                data-testid={`users-delete-btn-${row.id}`}
                                                className="p-2 rounded-lg text-rose-600 hover:bg-rose-50 transition-colors"
                                                title="Hapus"
                                            ><Trash2 className="w-4 h-4" /></button>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {/* Create/Edit */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Edit Pengguna" : "Tambah Pengguna"}</DialogTitle>
                        <DialogDescription>
                            {editing ? "Perbarui data pengguna." : "Buat akun pengguna baru untuk sistem."}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <Label>Username <span className="text-rose-600">*</span></Label>
                            <Input
                                value={form.username}
                                onChange={(e) => setForm({ ...form, username: e.target.value.toUpperCase() })}
                                disabled={!!editing}
                                data-testid="users-form-username"
                                className="mt-1.5 rounded-xl uppercase"
                                placeholder="Contoh: BUDI"
                            />
                        </div>
                        <div>
                            <Label>Nama Lengkap <span className="text-rose-600">*</span></Label>
                            <Input
                                value={form.nama}
                                onChange={(e) => setForm({ ...form, nama: e.target.value })}
                                data-testid="users-form-nama"
                                className="mt-1.5 rounded-xl"
                                placeholder="Nama tampilan"
                            />
                        </div>
                        <div>
                            <Label>Role</Label>
                            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                <SelectTrigger data-testid="users-form-role" className="mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="admin">Administrator</SelectItem>
                                    <SelectItem value="staf_input">Staf Input</SelectItem>
                                    <SelectItem value="staf_cek">Staf Cek</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {!editing && (
                            <div>
                                <Label>Password <span className="text-rose-600">*</span></Label>
                                <Input
                                    type="password"
                                    value={form.password}
                                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                                    data-testid="users-form-password"
                                    className="mt-1.5 rounded-xl"
                                    placeholder="Min. 6 karakter"
                                />
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Batal</Button>
                        <Button onClick={save} disabled={submitting} data-testid="users-modal-save-button" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">
                            {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menyimpan...</> : "Simpan"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete */}
            <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
                <AlertDialogContent className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus Pengguna</AlertDialogTitle>
                        <AlertDialogDescription>
                            Yakin ingin menghapus <span className="font-semibold text-slate-900">{confirmDelete?.username}</span>?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Batal</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={doDelete}
                            disabled={deleting}
                            data-testid="users-confirm-delete"
                            className="bg-rose-600 hover:bg-rose-700 text-white rounded-xl"
                        >
                            {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Menghapus...</> : "Hapus"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Reset password */}
            <Dialog open={!!resetOpen} onOpenChange={(o) => { if (!o) { setResetOpen(null); setNewPassword(""); } }}>
                <DialogContent className="bg-white/95 backdrop-blur-2xl border-slate-200 rounded-2xl">
                    <DialogHeader>
                        <DialogTitle>Reset Password</DialogTitle>
                        <DialogDescription>Reset password untuk <span className="font-semibold text-slate-900">{resetOpen?.username}</span></DialogDescription>
                    </DialogHeader>
                    <div className="py-2">
                        <Label>Password Baru</Label>
                        <Input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            data-testid="users-reset-password-input"
                            className="mt-1.5 rounded-xl"
                            placeholder="Min. 6 karakter"
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setResetOpen(null); setNewPassword(""); }} className="rounded-xl">Batal</Button>
                        <Button onClick={doReset} data-testid="users-reset-submit" className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl">Reset</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
